import React, { useEffect, useState, useCallback } from "react";
import axios from "utils/axiosConfig";
import { useSelector } from "react-redux";
import uri from "../../features/config";
import AdminLayout from "../AdminLayout";
import { HiOutlineRefresh, HiOutlineSearch } from "react-icons/hi";
import { useToast } from "../../components/Toast";
import SidePanel from "../../components/SidePanel";

const OrderManagement = () => {
  const toast = useToast();
  const { user } = useSelector((state) => state.auth);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${uri}/api/admin/orders`, {
        headers: { "x-user-email": user?.data?.email },
      });
      setOrders(response.data.data);
    } catch (error) {
      toast.error("Telemetry sync failed");
    } finally {
      setLoading(false);
    }
  }, [user, toast]);

  useEffect(() => {
    if (user?.data?.email) fetchOrders();
  }, [user, fetchOrders]);

  const updateStatus = async (orderId, status) => {
    try {
      await axios.put(
        `${uri}/api/admin/orders/${orderId}`,
        { status },
        { headers: { "x-user-email": user?.data?.email } }
      );
      toast.success(`Tactical status: ${status}`);
      fetchOrders();
    } catch (error) {
      toast.error("Status synchronization failed");
    }
  };

  const statusColors = {
    pending:
      "bg-amber-500/10 text-amber-400 border-amber-500/20 shadow-[0_0_10px_rgba(245,158,11,0.1)]",
    processing:
      "bg-blue-500/10 text-blue-400 border-blue-500/20 shadow-[0_0_10px_rgba(59,130,246,0.1)]",
    shipped:
      "bg-purple-500/10 text-purple-400 border-purple-500/20 shadow-[0_0_10px_rgba(168,85,247,0.1)]",
    delivered:
      "bg-green-500/10 text-green-400 border-green-500/20 shadow-[0_0_10px_rgba(34,197,94,0.1)]",
    cancelled:
      "bg-red-500/10 text-red-400 border-red-500/20 shadow-[0_0_10px_rgba(239,68,68,0.1)]",
  };

  return (
    <AdminLayout>
      <div className="space-y-12 animate-fadeIn">
        <header className="flex flex-col md:flex-row md:items-end justify-between gap-8">
          <div className="space-y-4">
            <div className="inline-flex items-center gap-2 px-3 py-1 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-[10px] font-black text-emerald-400 uppercase tracking-widest">
              Live_Logistics_Pulse: Active
            </div>
            <h1 className="text-4xl md:text-6xl font-black text-white uppercase italic tracking-tighter">
              Tactical <span className="text-cyan-400">Orders</span>
            </h1>
            <p className="text-gray-500 font-bold uppercase tracking-[0.2em] text-[10px]">
              Real-time hardware distribution and fulfillment monitoring.
            </p>
          </div>
          <button
            onClick={fetchOrders}
            className="p-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl transition-all text-cyan-400 group"
          >
            <HiOutlineRefresh
              size={24}
              className={
                loading
                  ? "animate-spin"
                  : "group-hover:rotate-180 transition-transform duration-500"
              }
            />
          </button>
        </header>

        {/* Search & Intelligence */}
        <div className="relative group">
          <div className="absolute -inset-0.5 bg-gradient-to-r from-cyan-500 to-purple-600 rounded-[2rem] blur opacity-10 group-focus-within:opacity-30 transition-opacity" />
          <div className="relative flex items-center bg-[#111827]/40 backdrop-blur-xl border border-white/10 rounded-[2rem] p-2">
            <div className="pl-6 pr-4 text-cyan-500">
              <HiOutlineSearch size={22} />
            </div>
            <input
              type="text"
              placeholder="Search by operation code or recipient name..."
              className="flex-1 bg-transparent border-none py-4 text-white placeholder:text-gray-600 font-black italic tracking-tighter focus:ring-0 outline-none text-sm"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        {/* Global Logistics Grid */}
        <div className="bg-[#111827]/40 backdrop-blur-2xl rounded-[3rem] border border-white/10 overflow-hidden shadow-2xl">
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-white/[0.02] border-b border-white/5 text-gray-500 text-[10px] font-black tracking-[0.3em] uppercase italic">
                  <th className="px-10 py-8">Transmission_ID</th>
                  <th className="px-10 py-8">Recipient_Alias</th>
                  <th className="px-10 py-8">Loadout_Value</th>
                  <th className="px-10 py-8">Deployment_Status</th>
                  <th className="px-10 py-8 text-right">Intelligence</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-white/5 text-xs font-bold">
                {orders
                  .filter(
                    (o) =>
                      o._id.toLowerCase().includes(searchTerm.toLowerCase()) ||
                      o.user?.name
                        ?.toLowerCase()
                        .includes(searchTerm.toLowerCase())
                  )
                  .map((order) => (
                    <tr
                      key={order._id}
                      className="hover:bg-white/[0.03] transition-all duration-300 group"
                    >
                      <td className="px-10 py-8">
                        <p className="text-gray-400 font-mono tracking-tighter">
                          #{order._id.slice(-8).toUpperCase()}
                        </p>
                        <p className="text-[9px] text-gray-600 uppercase mt-1">
                          TS_{new Date(order.createdAt).getTime()}
                        </p>
                      </td>
                      <td className="px-10 py-8 font-black text-white italic uppercase">
                        {order.user?.name || "REDACTED"}
                      </td>
                      <td className="px-10 py-8 text-white font-black italic tracking-tighter text-base">
                        ${order.totalAmount}
                      </td>
                      <td className="px-10 py-8">
                        <select
                          value={order.status}
                          onChange={(e) =>
                            updateStatus(order._id, e.target.value)
                          }
                          className={`px-4 py-2 rounded-xl text-[10px] font-black uppercase tracking-widest border transition-all cursor-pointer outline-none appearance-none ${
                            statusColors[order.status] ||
                            "bg-white/5 text-gray-400 border-white/10"
                          }`}
                        >
                          <option value="pending" className="bg-[#0b0e14]">
                            PNDG
                          </option>
                          <option value="processing" className="bg-[#0b0e14]">
                            PRCS
                          </option>
                          <option value="shipped" className="bg-[#0b0e14]">
                            SHPD
                          </option>
                          <option value="delivered" className="bg-[#0b0e14]">
                            DLVD
                          </option>
                          <option value="cancelled" className="bg-[#0b0e14]">
                            CNCL
                          </option>
                        </select>
                      </td>
                      <td className="px-10 py-8 text-right">
                        <button
                          onClick={() => {
                            setSelectedOrder(order);
                            setIsPanelOpen(true);
                          }}
                          className="px-6 py-3 bg-white/5 hover:bg-white/10 border border-white/10 text-purple-400 rounded-xl transition-all text-[10px] font-black uppercase tracking-widest italic group-hover:shadow-[0_0_15px_rgba(168,85,247,0.2)]"
                        >
                          View Blueprint
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
          {orders.length === 0 && !loading && (
            <div className="py-24 text-center">
              <p className="text-gray-500 font-black uppercase tracking-[0.3em] italic text-xs">
                No active logistic transmissions intercepted.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Order Details SidePanel */}
      <SidePanel
        isOpen={isPanelOpen}
        onClose={() => setIsPanelOpen(false)}
        title="Fulfillment Blueprint"
      >
        {selectedOrder && (
          <div className="space-y-12">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="p-6 bg-white/5 rounded-2xl border border-white/10 overflow-hidden">
                <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2 italic">
                  Operation_Code
                </p>
                <p
                  className="text-lg font-black text-white italic truncate"
                  title={selectedOrder._id}
                >
                  #{selectedOrder._id.slice(-8).toUpperCase()}
                </p>
                <p className="text-[9px] font-mono text-gray-600 mt-1 truncate">
                  Full: {selectedOrder._id}
                </p>
              </div>
              <div className="p-6 bg-white/5 rounded-2xl border border-white/10">
                <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-2 italic">
                  Matrix_Status
                </p>
                <div
                  className={`inline-block px-3 py-1.5 rounded-lg text-[10px] font-black uppercase tracking-widest border ${
                    statusColors[selectedOrder.status]
                  }`}
                >
                  {selectedOrder.status}
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <h4 className="text-[10px] font-black text-white uppercase tracking-[0.4em] italic mb-6 flex items-center gap-4">
                Manifest_Loadout <span className="flex-1 h-px bg-white/10" />
              </h4>
              <div className="space-y-4">
                {selectedOrder.items?.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-6 p-4 bg-white/[0.02] rounded-2xl border border-white/5"
                  >
                    <div className="w-16 h-16 bg-white/5 rounded-xl p-1 flex items-center justify-center">
                      <img
                        src={item.image}
                        alt=""
                        className="max-h-full object-contain"
                      />
                    </div>
                    <div className="flex-1">
                      <p className="text-white font-black italic uppercase text-sm">
                        {item.name}
                      </p>
                      <p className="text-[10px] text-gray-600 font-bold uppercase tracking-widest mt-1">
                        QTY: {item.quantity || 1} x ${item.price}
                      </p>
                    </div>
                    <p className="text-cyan-400 font-black italic tracking-tighter">
                      ${(item.quantity || 1) * item.price}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div className="p-8 bg-cyan-500/5 rounded-3xl border border-cyan-500/20">
              <div className="flex justify-between items-end">
                <div>
                  <p className="text-[10px] font-black text-cyan-500 uppercase tracking-widest mb-1 italic">
                    Total_Valuation
                  </p>
                  <p className="text-4xl font-black text-white italic tracking-tighter">
                    ${selectedOrder.totalAmount || selectedOrder.total}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-[10px] font-black text-gray-500 uppercase tracking-widest mb-1 italic">
                    Deployment_Vector
                  </p>
                  <p className="text-xs text-gray-400 font-bold uppercase">
                    {selectedOrder.address || "Standard Secure Drop"}
                  </p>
                </div>
              </div>
            </div>

            <div className="pt-6">
              <button
                onClick={() => setIsPanelOpen(false)}
                className="w-full py-6 bg-white/5 text-gray-400 font-black rounded-2xl hover:bg-white/10 transition-all uppercase tracking-widest text-[10px] italic border border-white/5"
              >
                Close Intelligence File
              </button>
            </div>
          </div>
        )}
      </SidePanel>
    </AdminLayout>
  );
};

export default OrderManagement;
